import { useEffect, useMemo, useState } from "react";
import { usePage } from "@inertiajs/react";
import { router } from "@inertiajs/react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { WordDetail, WordDetailSkeleton } from "@/components/word/WordDetail";
import { fetchWord } from "@/api/words";
import { fetchLanguage } from "@/api/languages";
import { useResource } from "@/hooks/useResource";
import type { SharedProps } from "@/types";
import type { BreadcrumbItem } from "@/components/ui/Breadcrumbs";
import { PageContainer } from '@/components/layout/PageContainer'
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage";

interface Props {
  id: number;
  language_id: number;
}

interface ReadingBack {
  readingId: number;
  readingTitle: string;
  scrollY: number;
}

export default function WordsShow() {
  const { id, language_id } = usePage().props as unknown as SharedProps & Props;
  useCurrentLanguage(language_id);
  const [readingBack, setReadingBack] = useState<ReadingBack | null>(null);

  const { data, loading } = useResource(
    () =>
      Promise.all([
        fetchWord(language_id, id).then((res) => res.data.data),
        fetchLanguage(language_id).then((res) => res.data.data),
      ]).then(([word, language]) => ({ word, language })),
    [language_id, id],
  );
  const word = data?.word ?? null;
  const language = data?.language ?? null;

  useEffect(() => {
    const stored = sessionStorage.getItem("word_detail_back");
    if (stored) {
      setReadingBack(JSON.parse(stored) as ReadingBack);
    }
  }, []);

  function handleBackToText() {
    if (!readingBack) return;
    sessionStorage.setItem(
      "reading_scroll_restore",
      JSON.stringify(readingBack.scrollY),
    );
    sessionStorage.removeItem("word_detail_back");
    router.visit(`/readings/${readingBack.readingId}`);
  }

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [];
    if (language) {
      items.push({ label: language.name, href: `/languages/${language_id}` });
    }
    if (readingBack) {
      items.push({
        label: readingBack.readingTitle,
        onClick: handleBackToText,
      });
    }
    if (word) {
      items.push({ label: word.lemma });
    }
    return items;
  }, [language, readingBack, word, language_id]);

  return (
    <AppLayout>
      <PageContainer className="py-10 sm:py-14 lg:py-16">
        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <div className="animate-enter mb-8">
            <Breadcrumbs items={breadcrumbs} />
          </div>
        )}

        {loading && <WordDetailSkeleton />}

        {!loading && !word && (
          <p className="font-serif text-base text-muted text-center py-16">
            Word not found.
          </p>
        )}

        {!loading && word && <WordDetail word={word} language={language} />}
      </PageContainer>
    </AppLayout>
  );
}
